import axios from "axios";
import { env } from "@/config/env";
import { api } from "../../api";
import { authSession } from "@/utils/authSession";
import type {
  AuthApiResponse,
  AuthResponse,
  AuthType,
  ChangePasswordType,
  ResetPasswordType,
  SendSecurityCodeType,
  ValidateOtpType,
} from "./authType";

/**
 * Extrai a mensagem de erro no shape padrão da API
 * (`{ message: string[] }`) priorizando a primeira mensagem legível.
 */
const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (Array.isArray(message) && message.length > 0) return message[0];
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

const handleAuth = async ({
  password,
  phone,
}: AuthType): Promise<AuthResponse> => {
  try {
    const { data } = await api.post<AuthApiResponse>("v1/auth/sign-in", {
      password,
      phone,
    });

    return {
      accessToken: data["access_token"] ?? "",
      refreshToken: data["refresh_token"] ?? "",
      features: data.features,
      session: data.session,
    };
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Erro ao autenticar usuário"));
  }
};

const handleChangePassword = async ({
  new_password,
  old_password,
  phone,
}: ChangePasswordType): Promise<AuthResponse> => {
  const { data } = await api.post<AuthApiResponse>("v1/auth/change-password", {
    new_password,
    old_password,
    phone,
  });

  return {
    accessToken: data["access_token"] ?? "",
    refreshToken: data["refresh_token"] ?? "",
    session: data.session,
  };
};

// Solicita o envio do código de recuperação por WhatsApp. Sucesso = 201 sem
// body. Reutilizado pelo botão "Reenviar código".
const handleForgotPassword = async (phone: string): Promise<void> => {
  try {
    await api.post("v1/auth/forgot-password", { phone });
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Não foi possível enviar o código")
    );
  }
};

// Valida o código de recuperação sem redefinir a senha (passo opcional, usado
// para dar feedback imediato antes da tela de nova senha).
const handleSendSecurityCode = async ({
  phone,
  code,
}: SendSecurityCodeType): Promise<void> => {
  try {
    await api.post("v1/auth/forgot-password/send-security-code", {
      phone,
      code,
    });
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Não foi possível validar o código")
    );
  }
};

// Redefine a senha com telefone + código + nova senha. Retorna o mesmo payload
// do sign-in (token + refresh token + features) para já autenticar o usuário.
const handleResetPassword = async ({
  phone,
  code,
  newPassword,
}: ResetPasswordType): Promise<AuthResponse> => {
  try {
    const { data } = await api.post<AuthApiResponse>(
      "v1/auth/forgot-password/reset-password",
      {
        phone,
        code,
        new_password: newPassword,
      }
    );

    return {
      accessToken: data["access_token"] ?? "",
      refreshToken: data["refresh_token"] ?? "",
      features: data.features,
      session: data.session,
    };
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Não foi possível redefinir a senha")
    );
  }
};

// Solicita o envio do código OTP por WhatsApp. Chama o backend direto (como o
// sign-in) com o header `key`. Sucesso = 201 sem body.
const handleRequestOtp = async (phone: string): Promise<void> => {
  try {
    await api.post(
      "v1/auth/opt",
      { phone },
      { headers: { key: env.AUTH_OPT_KEY } }
    );
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Não foi possível enviar o código")
    );
  }
};

// Valida telefone + código e autentica o usuário. Retorna o mesmo payload do
// sign-in (token + refresh token + features).
const handleValidateOtp = async ({
  phone,
  code,
}: ValidateOtpType): Promise<AuthResponse> => {
  try {
    const { data } = await api.post<AuthApiResponse>("v1/auth/opt/validate", {
      phone,
      code,
    });

    return {
      accessToken: data["access_token"] ?? "",
      refreshToken: data["refresh_token"] ?? "",
      features: data.features,
      session: data.session,
    };
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Não foi possível validar o código")
    );
  }
};

// Revoga o refresh token no servidor (POST /auth/logout). Sem isso, limpar o
// storage não encerra nada: um par access+refresh copiado antes do logout
// continuaria renovando a sessão até a validade absoluta.
const handleLogout = async (): Promise<void> => {
  await api.post(
    authSession.isCookieMode() ? "v1/auth/cookie-logout" : "v1/auth/logout"
  );
};

export const authService = {
  handleAuth,
  handleLogout,
  handleChangePassword,
  handleForgotPassword,
  handleSendSecurityCode,
  handleResetPassword,
  handleRequestOtp,
  handleValidateOtp,
};
